import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Chart, Line } from 'react-chartjs-2';
import Modal from 'react-modal';
import styles from './NewControlChart.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import {
    calculateCapabilityMetrics,
    buildControlNormalDistributionChartData,
    buildControlNormalDistributionOptions,
} from './controlChart/controlChartData';
import {
    exportChartAsPNG,
    exportStepTableAsCSV,
    exportStepTableAsPDF,
} from './controlChart/controlChartExport';
import {
    artavaChartJsAxisColor,
    artavaChartJsGridColor,
    artavaChartJsLegendColor,
    artavaChartJsTickColor,
    artavaChartJsTitleColor,
    artavaChartJsTooltip,
} from './chartPalette';

const CONTROL_MODAL_SIZES = [
    { key: 'medium', label: 'Medium' },
    { key: 'large', label: 'Large' },
    { key: 'full', label: 'Full screen' },
];
const CONTROL_CHART_RESULTS_STORAGE_KEY = 'smarti:last-control-chart-results';

const formatValue = (value, digits = 4) => (Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : 'N/A');

const toFiniteNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(parsed);
};

const buildCriticalityReason = ({
    outOfSpecLow,
    outOfSpecHigh,
    outOfControlLow,
    outOfControlHigh,
    nearSpec,
    nearControl,
}) => {
    const reasons = [];
    if (outOfSpecLow) reasons.push('Below LSL');
    if (outOfSpecHigh) reasons.push('Above USL');
    if (outOfControlLow) reasons.push('Below LCL');
    if (outOfControlHigh) reasons.push('Above UCL');
    if (!reasons.length && nearSpec) reasons.push('Near specification limits');
    if (!reasons.length && nearControl) reasons.push('Near control limits');
    return reasons.length ? reasons.join(' + ') : 'Within limits';
};

const buildCapabilityMetricCards = (metrics) => [
    { label: 'LSL', value: formatValue(metrics?.lsl, 4) },
    { label: 'USL', value: formatValue(metrics?.usl, 4) },
    { label: 'Mean', value: formatValue(metrics?.mean, 4) },
    { label: 'Sigma within', value: formatValue(metrics?.sigmaWithin, 4) },
    { label: 'Sigma overall', value: formatValue(metrics?.sigmaOverall, 4) },
    { label: 'Cp', value: formatValue(metrics?.cp, 3) },
    { label: 'Cpu', value: formatValue(metrics?.cpu, 3) },
    { label: 'Cpl', value: formatValue(metrics?.cpl, 3) },
    { label: 'Cpk', value: formatValue(metrics?.cpk, 3) },
    { label: 'Pp', value: formatValue(metrics?.pp, 3) },
    { label: 'Ppu', value: formatValue(metrics?.ppu, 3) },
    { label: 'Ppl', value: formatValue(metrics?.ppl, 3) },
    { label: 'Ppk', value: formatValue(metrics?.ppk, 3) },
    { label: 'k', value: formatValue(metrics?.k, 3) },
    { label: 'Z bench', value: formatValue(metrics?.zBench, 3) },
];

const computeSeriesStats = (rows = [], overrides = {}) => {
    const numericRows = rows
        .map((row, index) => ({
            ...row,
            observation: index + 1,
            value: Number(row.measureValue),
        }))
        .filter((row) => Number.isFinite(row.value));

    const values = numericRows.map((row) => row.value);
    const sampleCount = values.length;
    const meanValue = sampleCount ? values.reduce((sum, value) => sum + value, 0) / sampleCount : 0;
    const variance = sampleCount
        ? values.reduce((sum, value) => sum + ((value - meanValue) ** 2), 0) / sampleCount
        : 0;
    const sigmaValue = Math.sqrt(variance);
    const movingRanges = values.slice(1).map((value, index) => Math.abs(value - values[index]));
    const mrBar = movingRanges.length
        ? movingRanges.reduce((sum, value) => sum + value, 0) / movingRanges.length
        : 0;
    const computedUcl = meanValue + (sigmaValue * 3);
    const computedLcl = meanValue - (sigmaValue * 3);
    const upperControlLimit = Number.isFinite(overrides.upper) ? overrides.upper : computedUcl;
    const lowerControlLimit = Number.isFinite(overrides.lower) ? overrides.lower : computedLcl;
    const mrUcl = mrBar * 3.267;

    return {
        rows: numericRows,
        values,
        sampleCount,
        meanValue,
        sigmaValue,
        movingRanges,
        mrBar,
        mrUcl,
        upperControlLimit,
        lowerControlLimit,
    };
};

const annotateRows = (rows = [], limits = {}) => rows.map((row) => {
    const value = toFiniteNumber(row.value ?? row.measureValue);
    const mean = toFiniteNumber(limits.mean);
    const lowerControlLimit = toFiniteNumber(limits.lowerControlLimit);
    const upperControlLimit = toFiniteNumber(limits.upperControlLimit);
    const lowerSpecLimit = toFiniteNumber(limits.lowerSpecLimit ?? row.limitLow);
    const upperSpecLimit = toFiniteNumber(limits.upperSpecLimit ?? row.limitHigh);
    const specSpan = Number.isFinite(lowerSpecLimit) && Number.isFinite(upperSpecLimit)
        ? Math.abs(upperSpecLimit - lowerSpecLimit)
        : null;
    const controlSpan = Number.isFinite(lowerControlLimit) && Number.isFinite(upperControlLimit)
        ? Math.abs(upperControlLimit - lowerControlLimit)
        : null;
    const nearestSpecDistance = Number.isFinite(value)
        ? Math.min(
            Number.isFinite(lowerSpecLimit) ? Math.abs(value - lowerSpecLimit) : Number.POSITIVE_INFINITY,
            Number.isFinite(upperSpecLimit) ? Math.abs(upperSpecLimit - value) : Number.POSITIVE_INFINITY
        )
        : Number.POSITIVE_INFINITY;
    const nearestControlDistance = Number.isFinite(value)
        ? Math.min(
            Number.isFinite(lowerControlLimit) ? Math.abs(value - lowerControlLimit) : Number.POSITIVE_INFINITY,
            Number.isFinite(upperControlLimit) ? Math.abs(upperControlLimit - value) : Number.POSITIVE_INFINITY
        )
        : Number.POSITIVE_INFINITY;

    const outOfSpecLow = Number.isFinite(value) && Number.isFinite(lowerSpecLimit) && value < lowerSpecLimit;
    const outOfSpecHigh = Number.isFinite(value) && Number.isFinite(upperSpecLimit) && value > upperSpecLimit;
    const outOfControlLow = Number.isFinite(value) && Number.isFinite(lowerControlLimit) && value < lowerControlLimit;
    const outOfControlHigh = Number.isFinite(value) && Number.isFinite(upperControlLimit) && value > upperControlLimit;
    const nearSpec = !outOfSpecLow && !outOfSpecHigh && Number.isFinite(specSpan) && specSpan > 0 && nearestSpecDistance <= (specSpan * 0.1);
    const nearControl = !outOfControlLow && !outOfControlHigh && Number.isFinite(controlSpan) && controlSpan > 0 && nearestControlDistance <= (controlSpan * 0.08);
    const isCritical = outOfSpecLow || outOfSpecHigh || outOfControlLow || outOfControlHigh;
    const isWarning = !isCritical && (nearSpec || nearControl);

    return {
        ...row,
        measuredAtLabel: formatDateTime(row.testStarted),
        deltaFromCl: Number.isFinite(value) && Number.isFinite(mean) ? Number((value - mean).toFixed(4)) : null,
        effectiveLsl: lowerSpecLimit,
        effectiveLcl: lowerControlLimit,
        effectiveUcl: upperControlLimit,
        effectiveUsl: upperSpecLimit,
        criticalityLabel: isCritical ? 'Critical' : isWarning ? 'Warning' : 'Stable',
        criticalityReason: buildCriticalityReason({
            outOfSpecLow,
            outOfSpecHigh,
            outOfControlLow,
            outOfControlHigh,
            nearSpec,
            nearControl,
        }),
        isCritical,
        isWarning,
        isOutOfSpec: outOfSpecLow || outOfSpecHigh,
        isOutOfControl: outOfControlLow || outOfControlHigh,
    };
});

const buildCriticalPointDataset = (rows = [], label = 'Critical points') => ({
    label,
    data: rows.map((row) => (row.isCritical ? row.value : null)),
    borderColor: 'transparent',
    backgroundColor: rows.map((row) => (
        row.isOutOfSpec ? '#dc2626' : row.isOutOfControl ? '#f97316' : 'transparent'
    )),
    pointBorderColor: rows.map((row) => (
        row.isOutOfSpec ? '#991b1b' : row.isOutOfControl ? '#9a3412' : 'transparent'
    )),
    pointRadius: rows.map((row) => (row.isCritical ? 5 : 0)),
    pointHoverRadius: rows.map((row) => (row.isCritical ? 7 : 0)),
    pointBorderWidth: rows.map((row) => (row.isCritical ? 2 : 0)),
    showLine: false,
    order: -1,
});

const buildArtavaLineChartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top',
            labels: {
                color: artavaChartJsLegendColor,
            },
        },
        title: {
            display: true,
            text: title,
            color: artavaChartJsTitleColor,
        },
        tooltip: artavaChartJsTooltip,
    },
    scales: {
        x: {
            grid: {
                color: artavaChartJsGridColor,
            },
            border: {
                color: artavaChartJsAxisColor,
            },
            ticks: {
                color: artavaChartJsTickColor,
            },
        },
        y: {
            grid: {
                color: artavaChartJsGridColor,
            },
            border: {
                color: artavaChartJsAxisColor,
            },
            ticks: {
                color: artavaChartJsTickColor,
            },
        },
    },
});

const readStoredControlChartPayload = () => {
    try {
        const raw = window.localStorage.getItem(CONTROL_CHART_RESULTS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
        console.warn('Could not read stored control-chart results:', error);
        return null;
    }
};

const persistControlChartPayload = (payload) => {
    try {
        window.localStorage.setItem(CONTROL_CHART_RESULTS_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
        console.warn('Could not persist control-chart payload:', error);
    }
};

const NewControlChart = ({ embedded = false, initialResults = null, initialMode = null }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const storedPayload = useMemo(() => readStoredControlChartPayload(), []);
    const routeState = location.state || {};
    const resolvedPayload = routeState?.results
        ? routeState
        : initialResults
            ? { results: initialResults, mode: initialMode }
            : storedPayload;
    const { results } = resolvedPayload || {};
    const analysisMode = resolvedPayload?.mode === 'burn-in' ? 'burn-in' : 'production';
    const modeConfig = analysisMode === 'burn-in'
        ? {
            noun: 'burn-in source file',
            nounPlural: 'burn-in source files',
            headerEyebrow: 'Calculated burn-in control limits',
            headerTitle: 'Selected Burn-In Files Control Analysis',
            headerDescription: 'This page rebuilds a full control-analysis workspace from the exact burn-in source files chosen in the Control Limits Calculator.',
            serialLabel: 'Unit filter',
            serialPlaceholder: 'Partial unit or serial',
            filenameLabel: 'Source file filter',
            filenamePlaceholder: 'Partial source file name',
            statusLabel: 'File status',
            versionLabel: 'Software version',
            testspecLabel: 'Sheet name',
            testspecAll: 'All sheets',
            tableTitle: 'Selected burn-in file detail table',
            csvTitle: 'Selected burn-in files',
            helpIntro: 'This page rebuilds a complete control-analysis workspace from the exact burn-in source files selected in the Burn-In Control Limits Calculator.',
        }
        : {
            noun: 'selected file',
            nounPlural: 'selected files',
            headerEyebrow: 'Calculated control limits',
            headerTitle: 'Selected Files Control Analysis',
            headerDescription: 'This page rebuilds a full control-chart workspace from the exact files chosen in the Control Limits Calculator.',
            serialLabel: 'Serial filter',
            serialPlaceholder: 'Partial serial number',
            filenameLabel: 'Filename filter',
            filenamePlaceholder: 'Partial filename',
            statusLabel: 'UUT status',
            versionLabel: 'ATE SW version',
            testspecLabel: 'Testspec',
            testspecAll: 'All testspecs',
            tableTitle: 'Selected-file detail table',
            csvTitle: 'Selected files',
            helpIntro: 'This page rebuilds a complete control-analysis workspace from the exact files selected in the Control Limits Calculator.',
        };
    const activeResults = useMemo(() => (
        results || {
            stepNumber: '',
            stepName: '',
            units: '',
            lsl: '',
            usl: '',
            individualChart: {},
            allRows: [],
            passRows: [],
            values: [],
            serialNumbers: [],
            filenames: [],
        }
    ), [results]);

    const passOnlyChartRef = useRef(null);
    const passOnlyExportRef = useRef(null);
    const passFailChartRef = useRef(null);
    const passFailExportRef = useRef(null);
    const normalChartRef = useRef(null);
    const normalExportRef = useRef(null);
    const mrChartRef = useRef(null);
    const mrExportRef = useRef(null);

    const [serialFilter, setSerialFilter] = useState('');
    const [filenameFilter, setFilenameFilter] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedAteSwVersion, setSelectedAteSwVersion] = useState('');
    const [selectedTestspec, setSelectedTestspec] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showTable, setShowTable] = useState(false);
    const [modalChartKey, setModalChartKey] = useState('');
    const [modalSize, setModalSize] = useState('large');
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isSelectedFilesModalOpen, setIsSelectedFilesModalOpen] = useState(false);
    const [openChartDetails, setOpenChartDetails] = useState({});
    const [includeDetailsInExport, setIncludeDetailsInExport] = useState({});
    const [tableSort, setTableSort] = useState({ key: 'criticalityLabel', direction: 'desc' });
    const [currentLsl, setCurrentLsl] = useState('');
    const [currentUsl, setCurrentUsl] = useState('');
    const [currentUcl, setCurrentUcl] = useState('');
    const [currentLcl, setCurrentLcl] = useState('');
    const [tempLsl, setTempLsl] = useState('');
    const [tempUsl, setTempUsl] = useState('');
    const [tempUcl, setTempUcl] = useState('');
    const [tempLcl, setTempLcl] = useState('');

    useEffect(() => {
        if (!results) {
            if (embedded) {
                return;
            }
            navigate('/control-chart');
            return;
        }
        persistControlChartPayload({
            mode: analysisMode,
            results,
        });
        const nextLsl = activeResults.lsl ?? '';
        const nextUsl = activeResults.usl ?? '';
        const nextUcl = activeResults.individualChart?.ucl ?? '';
        const nextLcl = activeResults.individualChart?.lcl ?? '';
        setCurrentLsl(nextLsl);
        setCurrentUsl(nextUsl);
        setCurrentUcl(nextUcl);
        setCurrentLcl(nextLcl);
        setTempLsl(nextLsl);
        setTempUsl(nextUsl);
        setTempUcl(nextUcl);
        setTempLcl(nextLcl);
    }, [activeResults, analysisMode, embedded, navigate, results]);

    const allRowsRaw = Array.isArray(activeResults.allRows)
        ? activeResults.allRows
        : (activeResults.values || []).map((value, index) => ({
            serialNumber: activeResults.serialNumbers?.[index] || '',
            filename: activeResults.filenames?.[index] || '',
            uutStatus: 'PASS',
            AteSwVersion: '',
            Testspec: '',
            testStarted: '',
            measureValue: Number(value),
            limitLow: activeResults.lsl ?? null,
            limitHigh: activeResults.usl ?? null,
        }));
    const passRowsRaw = Array.isArray(activeResults.passRows) && activeResults.passRows.length
        ? activeResults.passRows
        : allRowsRaw.filter((row) => String(row.uutStatus || '').trim().toUpperCase() === 'PASS');

    const availableStatuses = useMemo(
        () => [...new Set(allRowsRaw.map((row) => String(row.uutStatus || '').trim()).filter(Boolean))].sort(),
        [allRowsRaw]
    );
    const availableAteSwVersions = useMemo(
        () => [...new Set(allRowsRaw.map((row) => String(row.AteSwVersion || '').trim()).filter(Boolean))].sort(),
        [allRowsRaw]
    );
    const availableTestspecs = useMemo(
        () => [...new Set(allRowsRaw.map((row) => String(row.Testspec || '').trim()).filter(Boolean))].sort(),
        [allRowsRaw]
    );
    const selectedFileEntries = useMemo(() => {
        const seen = new Set();
        return allRowsRaw
            .map((row) => ({
                serialNumber: row.serialNumber || '',
                filename: row.filename || '',
                uutStatus: row.uutStatus || '',
                version: row.AteSwVersion || '',
                testspec: row.Testspec || '',
                measuredAt: row.testStarted || '',
            }))
            .filter((entry) => {
                const key = [entry.serialNumber, entry.filename, entry.uutStatus, entry.version, entry.testspec, entry.measuredAt].join('|||');
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .sort((left, right) => {
                const leftTime = new Date(left.measuredAt || 0).getTime() || 0;
                const rightTime = new Date(right.measuredAt || 0).getTime() || 0;
                return rightTime - leftTime;
            });
    }, [allRowsRaw]);
    const selectedFileFamilies = useMemo(
        () => [...new Set(selectedFileEntries.map((entry) => String(entry.filename || '').split('_')[0]).filter(Boolean))],
        [selectedFileEntries]
    );

    const passesSharedFilters = useCallback((row) => {
        const serialMatches = !serialFilter.trim() || String(row.serialNumber || '').toLowerCase().includes(serialFilter.trim().toLowerCase());
        const filenameMatches = !filenameFilter.trim() || String(row.filename || '').toLowerCase().includes(filenameFilter.trim().toLowerCase());
        const statusMatches = !selectedStatus || String(row.uutStatus || '') === selectedStatus;
        const versionMatches = !selectedAteSwVersion || String(row.AteSwVersion || '') === selectedAteSwVersion;
        const testspecMatches = !selectedTestspec || String(row.Testspec || '') === selectedTestspec;
        const rowDate = String(row.testStarted || '').slice(0, 10).replace(/\//g, '-');
        const startMatches = !startDate || (rowDate && rowDate >= startDate);
        const endMatches = !endDate || (rowDate && rowDate <= endDate);
        return serialMatches && filenameMatches && statusMatches && versionMatches && testspecMatches && startMatches && endMatches;
    }, [endDate, filenameFilter, selectedAteSwVersion, selectedStatus, selectedTestspec, serialFilter, startDate]);

    const filteredAllRows = useMemo(
        () => allRowsRaw.filter(passesSharedFilters),
        [allRowsRaw, passesSharedFilters]
    );
    const filteredPassRows = useMemo(
        () => passRowsRaw.filter(passesSharedFilters),
        [passRowsRaw, passesSharedFilters]
    );

    const resolvedLsl = currentLsl !== '' ? Number(currentLsl) : null;
    const resolvedUsl = currentUsl !== '' ? Number(currentUsl) : null;
    const resolvedUcl = currentUcl !== '' ? Number(currentUcl) : null;
    const resolvedLcl = currentLcl !== '' ? Number(currentLcl) : null;

    const passStats = useMemo(
        () => computeSeriesStats(filteredPassRows, { upper: resolvedUcl, lower: resolvedLcl }),
        [filteredPassRows, resolvedLcl, resolvedUcl]
    );
    const allStats = useMemo(
        () => computeSeriesStats(filteredAllRows, { upper: resolvedUcl, lower: resolvedLcl }),
        [filteredAllRows, resolvedLcl, resolvedUcl]
    );

    const annotatedPassRows = useMemo(
        () => annotateRows(passStats.rows, {
            mean: passStats.meanValue,
            lowerControlLimit: passStats.lowerControlLimit,
            upperControlLimit: passStats.upperControlLimit,
            lowerSpecLimit: resolvedLsl,
            upperSpecLimit: resolvedUsl,
        }),
        [passStats, resolvedLsl, resolvedUsl]
    );
    const annotatedAllRows = useMemo(
        () => annotateRows(allStats.rows, {
            mean: allStats.meanValue,
            lowerControlLimit: allStats.lowerControlLimit,
            upperControlLimit: allStats.upperControlLimit,
            lowerSpecLimit: resolvedLsl,
            upperSpecLimit: resolvedUsl,
        }),
        [allStats, resolvedLsl, resolvedUsl]
    );

    const capabilityPassOnly = useMemo(
        () => calculateCapabilityMetrics({ values: passStats.values, lsl: resolvedLsl, usl: resolvedUsl }),
        [passStats.values, resolvedLsl, resolvedUsl]
    );
    const capabilityPassFail = useMemo(
        () => calculateCapabilityMetrics({ values: allStats.values, lsl: resolvedLsl, usl: resolvedUsl }),
        [allStats.values, resolvedLsl, resolvedUsl]
    );
    const passOnlyDetailCards = useMemo(() => buildCapabilityMetricCards(capabilityPassOnly), [capabilityPassOnly]);
    const passFailDetailCards = useMemo(() => buildCapabilityMetricCards(capabilityPassFail), [capabilityPassFail]);
    const normalDetailCards = passOnlyDetailCards;
    const mrDetailCards = useMemo(() => ([
        { label: 'Samples', value: String(passStats.sampleCount) },
        { label: 'MR bar', value: formatValue(passStats.mrBar, 4) },
        { label: 'MR UCL', value: formatValue(passStats.mrUcl, 4) },
        { label: 'Mean', value: formatValue(capabilityPassOnly?.mean, 4) },
        { label: 'Sigma within', value: formatValue(capabilityPassOnly?.sigmaWithin, 4) },
        { label: 'Sigma overall', value: formatValue(capabilityPassOnly?.sigmaOverall, 4) },
        { label: 'Cp', value: formatValue(capabilityPassOnly?.cp, 3) },
        { label: 'Cpk', value: formatValue(capabilityPassOnly?.cpk, 3) },
        { label: 'Pp', value: formatValue(capabilityPassOnly?.pp, 3) },
        { label: 'Ppk', value: formatValue(capabilityPassOnly?.ppk, 3) },
    ]), [capabilityPassOnly, passStats]);

    const passOnlyChartData = useMemo(() => ({
        labels: annotatedPassRows.map((row) => row.serialNumber || `Obs ${row.observation}`),
        datasets: [
            {
                label: 'Individual values',
                data: annotatedPassRows.map((row) => row.value),
                borderColor: '#2563eb',
                backgroundColor: '#2563eb',
                pointRadius: 3,
                fill: false,
            },
            {
                label: 'CL',
                data: annotatedPassRows.map(() => passStats.meanValue),
                borderColor: '#16a34a',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'UCL',
                data: annotatedPassRows.map(() => passStats.upperControlLimit),
                borderColor: '#ef4444',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'LCL',
                data: annotatedPassRows.map(() => passStats.lowerControlLimit),
                borderColor: '#f59e0b',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'LSL',
                data: annotatedPassRows.map(() => resolvedLsl),
                borderColor: '#7c3aed',
                borderDash: [10, 6],
                pointRadius: 0,
            },
            {
                label: 'USL',
                data: annotatedPassRows.map(() => resolvedUsl),
                borderColor: '#8b5cf6',
                borderDash: [10, 6],
                pointRadius: 0,
            },
            buildCriticalPointDataset(annotatedPassRows),
        ],
    }), [annotatedPassRows, passStats, resolvedLsl, resolvedUsl]);

    const passFailChartData = useMemo(() => ({
        labels: annotatedAllRows.map((row) => row.serialNumber || `Obs ${row.observation}`),
        datasets: [
            {
                label: 'Individual values (PASS + FAIL)',
                data: annotatedAllRows.map((row) => row.value),
                borderColor: '#f97316',
                backgroundColor: '#f97316',
                pointRadius: 4,
                fill: false,
            },
            {
                label: 'CL',
                data: annotatedAllRows.map(() => allStats.meanValue),
                borderColor: '#16a34a',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'UCL',
                data: annotatedAllRows.map(() => allStats.upperControlLimit),
                borderColor: '#ef4444',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'LCL',
                data: annotatedAllRows.map(() => allStats.lowerControlLimit),
                borderColor: '#f59e0b',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'LSL',
                data: annotatedAllRows.map(() => resolvedLsl),
                borderColor: '#7c3aed',
                borderDash: [10, 6],
                pointRadius: 0,
            },
            {
                label: 'USL',
                data: annotatedAllRows.map(() => resolvedUsl),
                borderColor: '#8b5cf6',
                borderDash: [10, 6],
                pointRadius: 0,
            },
            buildCriticalPointDataset(annotatedAllRows),
        ],
    }), [allStats, annotatedAllRows, resolvedLsl, resolvedUsl]);

    const normalDistributionChartData = useMemo(
        () => buildControlNormalDistributionChartData({ values: passStats.values }),
        [passStats.values]
    );
    const normalDistributionChartOptions = useMemo(
        () => buildControlNormalDistributionOptions(normalDistributionChartData),
        [normalDistributionChartData]
    );

    const mrChartData = useMemo(() => ({
        labels: annotatedPassRows.slice(1).map((row) => row.serialNumber || `Obs ${row.observation}`),
        datasets: [
            {
                label: 'Moving Range',
                data: passStats.movingRanges,
                borderColor: '#7c3aed',
                backgroundColor: '#7c3aed',
                pointRadius: 3,
                fill: false,
            },
            {
                label: 'MR-bar (CL)',
                data: passStats.movingRanges.map(() => passStats.mrBar),
                borderColor: '#16a34a',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'UCL',
                data: passStats.movingRanges.map(() => passStats.mrUcl),
                borderColor: '#ef4444',
                borderDash: [5, 5],
                pointRadius: 0,
            },
            {
                label: 'Critical MR points',
                data: passStats.movingRanges.map((value) => (value > passStats.mrUcl ? value : null)),
                borderColor: 'transparent',
                backgroundColor: '#dc2626',
                pointBorderColor: '#991b1b',
                pointRadius: passStats.movingRanges.map((value) => (value > passStats.mrUcl ? 5 : 0)),
                pointHoverRadius: passStats.movingRanges.map((value) => (value > passStats.mrUcl ? 7 : 0)),
                pointBorderWidth: passStats.movingRanges.map((value) => (value > passStats.mrUcl ? 2 : 0)),
                showLine: false,
                order: -1,
            },
        ],
    }), [annotatedPassRows, passStats]);

    const passOnlyChartOptions = useMemo(() => buildArtavaLineChartOptions('Individual Chart (PASS only)'), []);
    const passFailChartOptions = useMemo(() => buildArtavaLineChartOptions('Individual Chart (PASS + FAIL)'), []);
    const mrChartOptions = useMemo(() => buildArtavaLineChartOptions('MR Chart'), []);

    const chartConfigs = useMemo(() => ([
        {
            key: 'pass-only',
            title: 'Individual Chart (PASS only)',
            description: `PASS-only measurements for the current ${modeConfig.noun} scope.`,
            chartRef: passOnlyChartRef,
            exportRef: passOnlyExportRef,
            data: passOnlyChartData,
            options: passOnlyChartOptions,
            Renderer: Line,
            detailCards: passOnlyDetailCards,
            fileName: `selected-files-pass-only-step-${activeResults.stepNumber}`,
            isEmpty: !annotatedPassRows.length,
        },
        {
            key: 'pass-fail',
            title: 'Individual Chart (PASS + FAIL)',
            description: `All measurements in the current ${modeConfig.noun} scope, including PASS and FAIL units.`,
            chartRef: passFailChartRef,
            exportRef: passFailExportRef,
            data: passFailChartData,
            options: passFailChartOptions,
            Renderer: Line,
            detailCards: passFailDetailCards,
            fileName: `selected-files-pass-fail-step-${activeResults.stepNumber}`,
            isEmpty: !annotatedAllRows.length,
        },
        {
            key: 'normal',
            title: 'Normal Distribution Chart',
            description: `Distribution of PASS-only measurements for the current ${modeConfig.noun} scope.`,
            chartRef: normalChartRef,
            exportRef: normalExportRef,
            data: normalDistributionChartData,
            options: normalDistributionChartOptions,
            Renderer: Chart,
            chartType: 'bar',
            detailCards: normalDetailCards,
            fileName: `selected-files-normal-distribution-step-${activeResults.stepNumber}`,
            isEmpty: !normalDistributionChartData.datasets?.length,
        },
        {
            key: 'mr',
            title: 'MR Chart',
            description: 'Moving-range behavior for PASS-only measurements.',
            chartRef: mrChartRef,
            exportRef: mrExportRef,
            data: mrChartData,
            options: mrChartOptions,
            Renderer: Line,
            detailCards: mrDetailCards,
            fileName: `selected-files-mr-step-${activeResults.stepNumber}`,
            isEmpty: !passStats.movingRanges.length,
        },
    ]), [
        annotatedAllRows.length,
        annotatedPassRows.length,
        mrChartData,
        mrChartOptions,
        mrDetailCards,
        modeConfig.noun,
        normalDetailCards,
        normalDistributionChartData,
        normalDistributionChartOptions,
        passFailChartData,
        passFailChartOptions,
        passFailDetailCards,
        passOnlyChartData,
        passOnlyChartOptions,
        passOnlyDetailCards,
        passStats.movingRanges.length,
        activeResults.stepNumber,
    ]);

    const activeModalChart = chartConfigs.find((chart) => chart.key === modalChartKey) || null;

    const tableColumns = useMemo(() => ([
        { key: 'criticalityLabel', label: 'Criticality' },
        { key: 'criticalityReason', label: 'Reason' },
        { key: 'serialNumber', label: 'Serial Number' },
        { key: 'filename', label: analysisMode === 'burn-in' ? 'Source File' : 'Filename' },
        { key: 'uutStatus', label: modeConfig.statusLabel },
        { key: 'stepResult', label: 'Step Result' },
        { key: 'AteSwVersion', label: modeConfig.versionLabel },
        { key: 'Testspec', label: modeConfig.testspecLabel },
        { key: 'measuredAtLabel', label: 'Measured At' },
        { key: 'observation', label: 'Observation' },
        { key: 'value', label: 'Value', format: (value) => formatValue(value, 6) },
        { key: 'deltaFromCl', label: 'Delta from CL', format: (value) => formatValue(value, 4) },
        { key: 'effectiveLsl', label: 'LSL', format: (value) => formatValue(value, 4) },
        { key: 'effectiveLcl', label: 'LCL', format: (value) => formatValue(value, 4) },
        { key: 'effectiveUcl', label: 'UCL', format: (value) => formatValue(value, 4) },
        { key: 'effectiveUsl', label: 'USL', format: (value) => formatValue(value, 4) },
    ]), [analysisMode, modeConfig.statusLabel, modeConfig.testspecLabel, modeConfig.versionLabel]);

    const tableRows = useMemo(() => {
        const rank = { Critical: 3, Warning: 2, Stable: 1 };
        const rows = [...annotatedAllRows];
        rows.sort((left, right) => {
            let leftValue = left?.[tableSort.key];
            let rightValue = right?.[tableSort.key];
            if (tableSort.key === 'criticalityLabel') {
                leftValue = rank[leftValue] ?? 0;
                rightValue = rank[rightValue] ?? 0;
            }
            const leftNumber = Number(leftValue);
            const rightNumber = Number(rightValue);
            let comparison = 0;
            if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftValue !== '' && rightValue !== '') {
                comparison = leftNumber - rightNumber;
            } else {
                comparison = String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, { numeric: true, sensitivity: 'base' });
            }
            return tableSort.direction === 'asc' ? comparison : -comparison;
        });
        return rows;
    }, [annotatedAllRows, tableSort]);

    const isApplyDisabled = useMemo(() => {
        const parsedLsl = tempLsl === '' ? null : Number(tempLsl);
        const parsedUsl = tempUsl === '' ? null : Number(tempUsl);
        return Number.isFinite(parsedLsl) && Number.isFinite(parsedUsl) && parsedLsl >= parsedUsl;
    }, [tempLsl, tempUsl]);

    const handleApplyLimits = () => {
        setCurrentLsl(tempLsl === '' ? '' : Number(tempLsl));
        setCurrentUsl(tempUsl === '' ? '' : Number(tempUsl));
        setCurrentUcl(tempUcl === '' ? '' : Number(tempUcl));
        setCurrentLcl(tempLcl === '' ? '' : Number(tempLcl));
    };

    const handleResetLimits = () => {
        setCurrentLsl(activeResults.lsl ?? '');
        setCurrentUsl(activeResults.usl ?? '');
        setCurrentUcl(activeResults.individualChart?.ucl ?? '');
        setCurrentLcl(activeResults.individualChart?.lcl ?? '');
        setTempLsl(activeResults.lsl ?? '');
        setTempUsl(activeResults.usl ?? '');
        setTempUcl(activeResults.individualChart?.ucl ?? '');
        setTempLcl(activeResults.individualChart?.lcl ?? '');
    };

    const toggleChartDetails = (chartKey) => {
        setOpenChartDetails((current) => ({ ...current, [chartKey]: !current[chartKey] }));
    };
    const toggleIncludeDetailsInExport = (chartKey) => {
        setIncludeDetailsInExport((current) => ({ ...current, [chartKey]: !current[chartKey] }));
    };
    const toggleTableSort = (columnKey) => {
        setTableSort((current) => {
            if (current.key === columnKey) {
                return { key: columnKey, direction: current.direction === 'desc' ? 'asc' : 'desc' };
            }
            return { key: columnKey, direction: columnKey === 'criticalityLabel' ? 'desc' : 'asc' };
        });
    };

    const modalSizeClass = modalSize === 'full'
        ? styles.modalSizeFull
        : modalSize === 'medium'
            ? styles.modalSizeMedium
            : styles.modalSizeLarge;
    const modalChartStageClass = modalSize === 'full'
        ? styles.modalChartStageFull
        : modalSize === 'medium'
            ? styles.modalChartStageMedium
            : styles.modalChartStageLarge;

    if (!results && embedded) {
        return (
            <div className={styles.container}>
                <header className={styles.pageHeader}>
                    <div>
                        <p className={styles.eyebrow}>Calculated control limits</p>
                        <h2 className={styles.headerTitle}>New Control Chart</h2>
                        <p className={styles.description}>
                            Run the Control Limits Calculator once from Control Chart or Burn-In Control Chart first. Smarti will then keep the last calculated scope available here for report capture.
                        </p>
                    </div>
                </header>
                <section className={styles.filterSection}>
                    <div className={styles.emptyState}>No calculated control-limit result is available yet.</div>
                </section>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {!embedded ? <GoToDashboardButton /> : null}

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>{modeConfig.headerEyebrow}</p>
                    <h2 className={styles.headerTitle}>{modeConfig.headerTitle} for Step {activeResults.stepNumber}</h2>
                    <p className={styles.description}>
                        {modeConfig.headerDescription}
                    </p>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.secondaryButton} onClick={() => setIsSelectedFilesModalOpen(true)}>
                        View selected files
                    </button>
                </div>
            </header>

            {analysisMode === 'production' && (
                <SourceFileSummary
                    compact
                    startDate={startDate || null}
                    endDate={endDate || null}
                    ateSwVersion={selectedAteSwVersion}
                    testspec={selectedTestspec}
                    selectedFamilies={[]}
                    title="Source files used for dashboard charts"
                />
            )}

            <section className={styles.metricGrid}>
                <div className={styles.metricCard}><span className={styles.metricLabel}>Step Name</span><strong className={styles.metricValue}>{activeResults.stepName || 'N/A'}</strong></div>
                <div className={styles.metricCard}><span className={styles.metricLabel}>Units</span><strong className={styles.metricValue}>{activeResults.units || 'N/A'}</strong></div>
                <div className={styles.metricCard}><span className={styles.metricLabel}>PASS-only count</span><strong className={styles.metricValue}>{passStats.sampleCount}</strong></div>
                <div className={styles.metricCard}><span className={styles.metricLabel}>PASS + FAIL count</span><strong className={styles.metricValue}>{allStats.sampleCount}</strong></div>
                <div className={styles.metricCard}><span className={styles.metricLabel}>Visible mean (PASS)</span><strong className={styles.metricValue}>{formatValue(passStats.meanValue, 4)}</strong></div>
                <div className={styles.metricCard}><span className={styles.metricLabel}>Visible mean (ALL)</span><strong className={styles.metricValue}>{formatValue(allStats.meanValue, 4)}</strong></div>
            </section>

            <section className={styles.filterSection}>
                <div className={styles.inputGroup}>
                    <label htmlFor="serialFilter">{modeConfig.serialLabel}</label>
                    <input id="serialFilter" type="text" placeholder={modeConfig.serialPlaceholder} value={serialFilter} onChange={(event) => setSerialFilter(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="filenameFilter">{modeConfig.filenameLabel}</label>
                    <input id="filenameFilter" type="text" placeholder={modeConfig.filenamePlaceholder} value={filenameFilter} onChange={(event) => setFilenameFilter(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="statusFilter">{modeConfig.statusLabel}</label>
                    <select id="statusFilter" value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)} className={styles.inputField}>
                        <option value="">All statuses</option>
                        {availableStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="ateFilter">{modeConfig.versionLabel}</label>
                    <select id="ateFilter" value={selectedAteSwVersion} onChange={(event) => setSelectedAteSwVersion(event.target.value)} className={styles.inputField}>
                        <option value="">All versions</option>
                        {availableAteSwVersions.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="testspecFilter">{modeConfig.testspecLabel}</label>
                    <select id="testspecFilter" value={selectedTestspec} onChange={(event) => setSelectedTestspec(event.target.value)} className={styles.inputField}>
                        <option value="">{modeConfig.testspecAll}</option>
                        {availableTestspecs.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="startDate">Start date</label>
                    <input id="startDate" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="endDate">End date</label>
                    <input id="endDate" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="lslInput">Manual LSL</label>
                    <input id="lslInput" type="number" placeholder={String(activeResults.lsl ?? '')} value={tempLsl ?? ''} onChange={(event) => setTempLsl(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="uslInput">Manual USL</label>
                    <input id="uslInput" type="number" placeholder={String(activeResults.usl ?? '')} value={tempUsl ?? ''} onChange={(event) => setTempUsl(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="uclInput">Manual UCL</label>
                    <input id="uclInput" type="number" placeholder={String(activeResults.individualChart?.ucl ?? '')} value={tempUcl ?? ''} onChange={(event) => setTempUcl(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="lclInput">Manual LCL</label>
                    <input id="lclInput" type="number" placeholder={String(activeResults.individualChart?.lcl ?? '')} value={tempLcl ?? ''} onChange={(event) => setTempLcl(event.target.value)} className={styles.inputField} />
                </div>
                <div className={styles.filterActions}>
                    <button onClick={handleApplyLimits} className={styles.applyButton} disabled={isApplyDisabled}>Apply limits</button>
                    <button onClick={handleResetLimits} className={styles.resetButton}>Reset limits</button>
                    <button onClick={() => setShowTable((value) => !value)} className={styles.secondaryButton}>{showTable ? 'Hide table' : 'Show table'}</button>
                    <button
                        onClick={() => exportStepTableAsCSV({ title: `${modeConfig.csvTitle} step ${activeResults.stepNumber}`, columns: tableColumns, rows: tableRows })}
                        className={styles.secondaryButton}
                    >
                        Export table CSV
                    </button>
                    <button
                        onClick={() => exportStepTableAsPDF({ title: `${modeConfig.csvTitle} step ${activeResults.stepNumber}`, columns: tableColumns, rows: tableRows })}
                        className={styles.secondaryButton}
                    >
                        Export table PDF
                    </button>
                </div>
                {isApplyDisabled && (
                    <p className={styles.errorText}>LSL cannot be greater than or equal to USL.</p>
                )}
            </section>

            {chartConfigs.map((chart) => {
                const Renderer = chart.Renderer;
                return (
                    <section key={chart.key} className={styles.chartSection}>
                        <div className={styles.chartHeader}>
                            <div>
                                <h3 className={styles.chartTitle}>{chart.title}</h3>
                                <p className={styles.description}>{chart.description}</p>
                            </div>
                            <div className={styles.chartActions}>
                                <button onClick={() => setModalChartKey(chart.key)} className={styles.expandButton} disabled={chart.isEmpty}>Expand</button>
                                <button onClick={() => toggleChartDetails(chart.key)} className={styles.secondaryButton}>{openChartDetails[chart.key] ? 'Hide details' : 'Details'}</button>
                                <label className={styles.exportCheckbox}>
                                    <input type="checkbox" checked={!!includeDetailsInExport[chart.key]} onChange={() => toggleIncludeDetailsInExport(chart.key)} />
                                    Include details in PNG
                                </label>
                                <button onClick={() => exportChartAsPNG(chart.exportRef, chart.fileName)} className={styles.exportButton} disabled={chart.isEmpty}>Export PNG</button>
                            </div>
                        </div>
                        <div ref={chart.exportRef} data-report-capture-root="true">
                            <div className={styles.chartContainer} ref={chart.chartRef}>
                                {chart.isEmpty ? (
                                    <div className={styles.emptyState}>No data available for this chart with the current selected-file scope.</div>
                                ) : chart.chartType ? (
                                    <Renderer type={chart.chartType} data={chart.data} options={chart.options} />
                                ) : (
                                    <Renderer data={chart.data} options={chart.options} />
                                )}
                            </div>
                            {(openChartDetails[chart.key] || includeDetailsInExport[chart.key]) && (
                                <div className={styles.chartMetricPanel}>
                                    <div className={styles.chartMetricGrid}>
                                        {chart.detailCards.map((metric) => (
                                            <div key={`${chart.key}-${metric.label}`} className={styles.chartMetricCard}>
                                                <span>{metric.label}</span>
                                                <strong>{metric.value}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                );
            })}

            {showTable && (
                <section className={styles.tableSection}>
                    <h3 className={styles.chartTitle}>{modeConfig.tableTitle}</h3>
                    <div className={styles.tableScroll}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    {tableColumns.map((column) => (
                                        <th key={column.key}>
                                            <button type="button" className={styles.tableSortButton} onClick={() => toggleTableSort(column.key)}>
                                                <span>{column.label}</span>
                                                <span className={styles.tableSortIndicator}>
                                                    {tableSort.key === column.key ? (tableSort.direction === 'asc' ? '↑' : '↓') : '↕'}
                                                </span>
                                            </button>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map((row, index) => (
                                    <tr
                                        key={`${row.serialNumber || 'row'}-${index}`}
                                        className={row.isCritical ? styles.tableRowCritical : row.isWarning ? styles.tableRowWarning : ''}
                                    >
                                        <td>{index + 1}</td>
                                        {tableColumns.map((column) => (
                                            <td key={column.key}>
                                                {column.key === 'criticalityLabel' ? (
                                                    <span className={row.isCritical ? styles.criticalBadge : row.isWarning ? styles.warningBadge : styles.stableBadge}>
                                                        {row[column.key]}
                                                    </span>
                                                ) : typeof column.format === 'function' ? (
                                                    column.format(row[column.key])
                                                ) : (
                                                    row[column.key] ?? 'N/A'
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <Modal
                isOpen={isSelectedFilesModalOpen}
                onRequestClose={() => setIsSelectedFilesModalOpen(false)}
                className={styles.helpModalContent}
                overlayClassName={styles.helpModalOverlay}
                contentLabel="Selected files"
            >
                <button onClick={() => setIsSelectedFilesModalOpen(false)} className={styles.closeHelpButton}>Close</button>
                <div className={styles.helpText}>
                    <h2>Selected files</h2>
                    <p>
                        {selectedFileEntries.length} {selectedFileEntries.length === 1 ? modeConfig.noun : modeConfig.nounPlural} were used to build this control-analysis workspace across {selectedFileFamilies.length} file family{selectedFileFamilies.length === 1 ? '' : 'ies'}.
                    </p>
                </div>
                <div className={styles.tableScroll}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{modeConfig.filenameLabel}</th>
                                <th>{modeConfig.serialLabel}</th>
                                <th>{modeConfig.statusLabel}</th>
                                <th>{modeConfig.versionLabel}</th>
                                <th>{modeConfig.testspecLabel}</th>
                                <th>Measured At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedFileEntries.map((entry, index) => (
                                <tr key={`${entry.filename}-${entry.serialNumber}-${index}`}>
                                    <td>{index + 1}</td>
                                    <td>{entry.filename || 'N/A'}</td>
                                    <td>{entry.serialNumber || 'N/A'}</td>
                                    <td>{entry.uutStatus || 'N/A'}</td>
                                    <td>{entry.version || 'N/A'}</td>
                                    <td>{entry.testspec || 'N/A'}</td>
                                    <td>{formatDateTime(entry.measuredAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Modal>

            <button className={styles.helpButton} onClick={() => setIsHelpOpen(true)} aria-label="Help">?</button>

            <Modal
                isOpen={!!activeModalChart}
                onRequestClose={() => {
                    setModalChartKey('');
                    setModalSize('large');
                }}
                className={`${styles.modalContent} ${modalSizeClass}`}
                overlayClassName={styles.modalOverlay}
            >
                <div className={styles.modalToolbar}>
                    <div className={styles.chartActions}>
                        {CONTROL_MODAL_SIZES.map((size) => (
                            <button
                                key={size.key}
                                type="button"
                                className={modalSize === size.key ? styles.applyButton : styles.secondaryButton}
                                onClick={() => setModalSize(size.key)}
                            >
                                {size.label}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => { setModalChartKey(''); setModalSize('large'); }} className={styles.closeModalButton}>Close</button>
                </div>
                {activeModalChart && (
                    <div className={styles.expandedChart}>
                        <h2>{activeModalChart.title}</h2>
                        <div className={`${styles.modalChartStage} ${modalChartStageClass}`}>
                            {activeModalChart.chartType ? (
                                <activeModalChart.Renderer type={activeModalChart.chartType} data={activeModalChart.data} options={{ ...activeModalChart.options, maintainAspectRatio: false }} />
                            ) : (
                                <activeModalChart.Renderer data={activeModalChart.data} options={{ ...activeModalChart.options, maintainAspectRatio: false }} />
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                isOpen={isHelpOpen}
                onRequestClose={() => setIsHelpOpen(false)}
                className={styles.helpModalContent}
                overlayClassName={styles.helpModalOverlay}
                contentLabel="Help Information"
            >
                <button onClick={() => setIsHelpOpen(false)} className={styles.closeHelpButton}>Close</button>
                <div className={styles.helpText}>
                    <h2>About this Page</h2>
                    <p>
                        {modeConfig.helpIntro}
                    </p>
                    <ul>
                        <li><strong>PASS-only chart:</strong> Uses only PASS units from the selected scope.</li>
                        <li><strong>PASS + FAIL chart:</strong> Uses the whole selected scope, including failing units.</li>
                        <li><strong>Normal distribution chart:</strong> Shows the PASS-only distribution shape.</li>
                        <li><strong>MR chart:</strong> Shows short-term point-to-point variation for PASS-only measurements.</li>
                        <li><strong>Filters:</strong> The page-level filters update the charts and the detail table together.</li>
                        <li><strong>Manual limits:</strong> You can override UCL, LCL, LSL, and USL directly on this page.</li>
                        <li><strong>Detail table:</strong> The table includes criticality, reasons, file context, limits, and timestamps for the selected dataset.</li>
                    </ul>
                </div>
            </Modal>
        </div>
    );
};

export default NewControlChart;
